
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BookOpen, GraduationCap, Users, BarChart2, Star, ShieldCheck, Settings, ArrowRight, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OnboardingPanelProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onFinish: () => void;
}

export function OnboardingPanel({ open, onOpenChange, onFinish }: Readonly<OnboardingPanelProps>) {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);

    const steps = [
        {
            id: 'welcome',
            icon: Star,
            title: t('common.onboarding.welcome.title'),
            description: t('common.onboarding.welcome.description'),
            color: 'text-warning',
            bgColor: 'bg-warning/10',
        },
        {
            id: 'learning',
            icon: BookOpen,
            title: t('common.onboarding.learning.title'),
            description: t('common.onboarding.learning.description'),
            color: 'text-primary',
            bgColor: 'bg-primary/10',
        },
        {
            id: 'simulation',
            icon: GraduationCap,
            title: t('common.onboarding.simulation.title'),
            description: t('common.onboarding.simulation.description'),
            color: 'text-accent-foreground',
            bgColor: 'bg-accent',
        },
        {
            id: 'partner',
            icon: Users,
            title: t('common.onboarding.partner.title'),
            description: t('common.onboarding.partner.description'),
            color: 'text-success',
            bgColor: 'bg-success/10',
        },
        {
            id: 'safety',
            icon: ShieldCheck,
            title: t('common.onboarding.safety.title', {
                defaultValue: 'Stay safe in chat',
            }),
            description: t('common.onboarding.safety.description', {
                defaultValue:
                    'Messages between users are encrypted. If someone behaves inappropriately, open the menu in the chat header to report or block them. Reports go to our moderation team; blocks stop the conversation in both directions.',
            }),
            color: 'text-success',
            bgColor: 'bg-success/10',
        },
        {
            id: 'results',
            icon: BarChart2,
            title: t('common.onboarding.results.title'),
            description: t('common.onboarding.results.description'),
            color: 'text-secondary-foreground',
            bgColor: 'bg-secondary',
        },
        {
            id: 'settings',
            icon: Settings,
            title: t('common.onboarding.settings.title', {
                defaultValue: 'Your settings',
            }),
            description: t('common.onboarding.settings.description', {
                defaultValue:
                    'Open Settings from the sidebar anytime to update your privacy preferences, review or change your plan, and delete your account if you ever need to.',
            }),
            color: 'text-primary',
            bgColor: 'bg-primary/10',
        },
    ];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onFinish();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSkip = () => {
        onFinish();
    };

    const CurrentIcon = steps[currentStep].icon;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden gap-0">
                <div className="h-2 bg-muted w-full">
                    <div
                        className="h-full bg-primary transition-all duration-300 ease-in-out"
                        style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                    />
                </div>

                <div className="p-5 sm:p-6">
                    <DialogHeader className="mb-4 sm:mb-6">
                        <div className="flex justify-center mb-4 sm:mb-6">
                            <div className={cn("p-3 sm:p-4 rounded-full transition-colors duration-300", steps[currentStep].bgColor)}>
                                <CurrentIcon className={cn("w-10 h-10 sm:w-12 sm:h-12 transition-colors duration-300", steps[currentStep].color)} />
                            </div>
                        </div>
                        <DialogTitle className="text-center text-xl sm:text-2xl font-bold font-ui">
                            {steps[currentStep].title}
                        </DialogTitle>
                        <DialogDescription className="text-center text-sm sm:text-base mt-2 font-ui">
                            {steps[currentStep].description}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex justify-center gap-2 mb-8">
                        {steps.map((step, index) => (
                            <div
                                key={step.id}
                                className={cn(
                                    "h-2 w-2 rounded-full transition-all duration-300",
                                    index === currentStep ? "bg-primary w-4" : "bg-muted"
                                )}
                            />
                        ))}
                    </div>

                    <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
                        <div className="flex w-full justify-between items-center">
                            <Button
                                variant="ghost"
                                onClick={handleSkip}
                                className="text-muted-foreground hover:text-foreground font-ui"
                            >
                                {t('common.onboarding.actions.skip')}
                            </Button>

                            <div className="flex gap-2">
                                {currentStep > 0 && (
                                    <Button variant="outline" onClick={handleBack} className="font-ui h-9 px-3 sm:h-10 sm:px-4">
                                        <ArrowLeft className="w-4 h-4 sm:mr-2" />
                                        <span className="hidden sm:inline">{t('common.onboarding.actions.back')}</span>
                                    </Button>
                                )}
                                <Button onClick={handleNext} className="font-ui h-9 px-3 sm:h-10 sm:px-4">
                                    {currentStep === steps.length - 1 ? (
                                        t('common.onboarding.actions.finish')
                                    ) : (
                                        <>
                                            <span className="mr-2">{t('common.onboarding.actions.next')}</span>
                                            <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}
